import React, { useMemo } from 'react';

interface MarkdownTextProps {
  content: string;
  className?: string;
}

/**
 * Component to render official Daggerheart formatting
 * Converts official markdown-style formatting to HTML
 * 
 * Supported patterns:
 * - **_Text_** → <strong><em>Text</em></strong> (Bold + Italic)
 * - **Text** → <strong>Text</strong> (Bold only)
 * - _Text_ → <em>Text</em> (Italic only)
 * - \n\n → Paragraph breaks
 */
export const MarkdownText: React.FC<MarkdownTextProps> = ({ content, className = '' }) => {
  const processedHTML = useMemo(() => {
    if (!content) return '';

    return content
      // Convert **_bold italic_** format (must come first to avoid conflicts)
      .replace(/\*\*_([^*]+)_\*\*/g, '<strong><em>$1</em></strong>')
      // Convert **bold** format
      .replace(/\*\*([^*_]+)\*\*/g, '<strong>$1</strong>')
      // Convert _italic_ format (must come after bold italic to avoid conflicts)
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      // Convert paragraph breaks
      .split('\n\n')
      .filter(paragraph => paragraph.trim() !== '')
      .map(paragraph => `<p>${paragraph.trim()}</p>`)
      .join('');
  }, [content]);

  // Don't render empty content
  if (!content || processedHTML === '') {
    return null;
  }

  return (
    <div 
      className={`markdown-text ${className}`}
      dangerouslySetInnerHTML={{ __html: processedHTML }}
    />
  );
};

export default MarkdownText;